import {logger} from './log.helper';

/**
 * ID of element that is used to get currently active language that will be used in translations.
 *
 * This element MUST have an attribute data-lang with one of the languages in UI_LANGUAGES. Example:
 *
 *   <meta id="angularjs-language" data-lang="fi" />
 *
 * @type {string}
 */
const LANGUAGE_ELEM_ID = 'angularjs-language';

/**
 * The languages the UI can be translated to.
 *
 * The first language listed here will be used as the default language
 * of the UI in case no language can be parsed.
 *
 * @type {string[]}
 */
const UI_LANGUAGES = ['fi', 'sv', 'en'];

/**
 * Locales for the aforementioned UI languages, for use with Intl.
 * @type {object.<string, string>}
 */
const INTL_LOCALES = {
  fi: 'fi-FI',
  sv: 'sv-SE',
  en: 'en-GB',
};

/**
 * Default language of the UI
 * @type {string}
 */
const DEFAULT_LANGUAGE = UI_LANGUAGES[0];

/**
 * The languages the user can ask for service in.
 *
 * Used to filter only personnel that speak the specified language.
 *
 * @type {{code: string, in_lang_trans: string}[]}
 */
const SERVICE_LANGUAGES = [
  { code: 'fi', in_lang_trans: 'suomi' },
  { code: 'sv', in_lang_trans: 'svenska' },
  { code: 'en', in_lang_trans: 'English' },
  { code: 'ru', in_lang_trans: 'русский' },
  { code: 'ro', in_lang_trans: 'românesc' },
  { code: 'de', in_lang_trans: 'Deutsch' },
  { code: 'fr', in_lang_trans: 'français' },
  { code: 'es', in_lang_trans: 'español' },
  { code: 'pt', in_lang_trans: 'português' },
  { code: 'et', in_lang_trans: 'eesti' },
  { code: 'it', in_lang_trans: 'italiano' },
  { code: 'pl', in_lang_trans: 'polski' },
  { code: 'ar', in_lang_trans: 'العربية' },
  { code: 'no', in_lang_trans: 'Norge' },
  { code: 'eu', in_lang_trans: 'Euskal' },
  { code: 'ne', in_lang_trans: 'नेपाली (nepalimaa)' },
  { code: 'tr', in_lang_trans: 'Türk' },
  { code: 'lt', in_lang_trans: 'Lietuvos' },
  { code: 'ur', in_lang_trans: 'ردو (urdu)' },
  { code: 'se', in_lang_trans: 'saami'},
  { code: 'uk', in_lang_trans: 'український'},
  { code: 'hu', in_lang_trans: 'magyar'},
  { code: 'lv', in_lang_trans: 'Latvijas'},
  { code: 'hi', in_lang_trans: 'हिन्दी (Hindī)'},
  { code: 'ku', in_lang_trans: 'کوردی (Kurdî)'},
  { code: 'fa', in_lang_trans: 'فارسی (farsi)'},
  { code: 'zh', in_lang_trans: '汉语/漢語 (Hànyǔ)'},
  { code: 'fi-native', in_lang_trans: 'suomi (äidinkieli)'},
  { code: 'sv-native', in_lang_trans: 'svenska (modersmål)'},
  { code: 'nl', in_lang_trans: 'Nederlands'},
];

/**
 * Get language name by language code.
 *
 * Used in the personnel listing.
 *
 * @param gettextCatalog gettextCatalog module of gettext
 * @param code {String} Language code
 *
 */
function getServiceLanguage(gettextCatalog, code) {
  const LANGUAGES = {
    /// Language name
    'fi': gettextCatalog.getString('suomi'),
    /// Language name
    'sv': gettextCatalog.getString('ruotsi'),
    /// Language name
    'en': gettextCatalog.getString('englanti'),
    /// Language name
    'ru': gettextCatalog.getString('venäjä'),
    /// Language name
    'ro': gettextCatalog.getString('romania'),
    /// Language name
    'de': gettextCatalog.getString('saksa'),
    /// Language name
    'fr': gettextCatalog.getString('ranska'),
    /// Language name
    'es': gettextCatalog.getString('espanja'),
    /// Language name
    'pt': gettextCatalog.getString('portugali'),
    /// Language name
    'et': gettextCatalog.getString('viro'),
    /// Language name
    'it': gettextCatalog.getString('italia'),
    /// Language name
    'pl': gettextCatalog.getString('puola'),
    /// Language name
    'ar': gettextCatalog.getString('arabia'),
    /// Language name
    'no': gettextCatalog.getString('norja'),
    /// Language name
    'eu': gettextCatalog.getString('baski'),
    /// Language name
    'ne': gettextCatalog.getString('nepali'),
    /// Language name
    'tr': gettextCatalog.getString('turkki'),
    /// Language name
    'ur': gettextCatalog.getString('urdu'),
    /// Language name
    'se': gettextCatalog.getString('saame'),
    /// Language name
    'lt': gettextCatalog.getString('liettua'),
    /// Language name
    'hu': gettextCatalog.getString('unkari'),
    /// Language name
    'uk': gettextCatalog.getString('ukraina'),
    /// Language name
    'hi': gettextCatalog.getString('hindi'),
    /// Language name
    'ku': gettextCatalog.getString('kurdi'),
    /// Language name
    'fa': gettextCatalog.getString('persia'),
    /// Language name
    'zh': gettextCatalog.getString('kiina'),
    /// Language name
    'fi-native': gettextCatalog.getString('suomi (äidinkieli)'),
    /// Language name
    'sv-native': gettextCatalog.getString('ruotsi (äidinkieli)'),
    /// Language name
    'nl': gettextCatalog.getString('hollanti'),
  };

  return LANGUAGES[code];
}

/**
 * Parse the current language from HTML.
 */
function parseLanguage() {
  const elem = document.getElementById(LANGUAGE_ELEM_ID);

  // If element is not found or is invalid, use default language
  if (!elem || !elem.dataset || !UI_LANGUAGES.includes(elem.dataset.lang)) {
    return DEFAULT_LANGUAGE;
  }

  return document.getElementById(LANGUAGE_ELEM_ID).dataset.lang;
}

const _language = parseLanguage();

/**
 * Log which language is used when AngularJS is starting up.
 */
function configLanguage() {
  'ngInject';

  logger().log(`Initialising with language "${_language}".`);
}

/**
 * Get currently active UI language.
 * @return {string}
 */
function getLanguage() {
  return _language;
}

/**
 * Get Intl locale matching currently active UI language.
 * @return {string}
 */
function getLocale() {
  return INTL_LOCALES[getLanguage()];
}

export {
  DEFAULT_LANGUAGE,
  UI_LANGUAGES,
  INTL_LOCALES,
  SERVICE_LANGUAGES,
  parseLanguage,
  configLanguage,
  getServiceLanguage,
  getLanguage,
  getLocale,
};
